import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import MenuIcon from '@mui/icons-material/Menu';
import RssFeedIcon from '@mui/icons-material/RssFeed';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import Backdrop from '../../../hoc/Backdrop';
import { resetBlogDetailState } from '../../blogs/slices/blogPageReducer';
import SideDrawerButtonComponent from '../components/SideDrawerButtonComponent';
import { setIsSideDrawerOpen } from '../slices/navigationReducer';

const CONVERSATIONS_LAST_VIEWED_KEY = 'conversationsLastViewedAt';
const ARTICLE_SUGGESTIONS_LAST_VIEWED_KEY = 'articleSuggestionsLastViewedAt';

const SideDrawer = (props: any) => {
  const { whichUserFlow } = props;

  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [isHomeSelected, setIsHomeSelected] = useState(false);
  const [isConversationsSelected, setIsConversationsSelected] = useState(false);
  const [isMyReadsSelected, setIsMyReadsSelected] = useState(false);
  const [isAboutSelected, setIsAboutSelected] = useState(false);
  const [isUploadSelected, setIsUploadSelected] = useState(false);
  const [isCreateCuratedListSelected, setIsCreateCuratedListSelected] =
    useState(false);
  const [isSuggestArticleSelected, setIsSuggestArticleSelected] = useState(false);
  const [isDonateSelected, setIsDonateSelected] = useState(false);
  const [isContactUsSelected, setIsContactUsSelected] = useState(false);
  const [isProfileSelected, setIsProfileSelected] = useState(false);
  const [isAdminSelected, setIsAdminSelected] = useState(false);
  const [isBlogSelected, setIsBlogSelected] = useState(false);
  const location = useLocation();

  const [totalBlogs, setTotalBlogs] = useState(0);
  const [bannedBlogs, setBannedBlogs] = useState(0);
  const [hasNewConversations, setHasNewConversations] = useState(false);
  const [hasNewArticleSuggestions, setHasNewArticleSuggestions] = useState(false);

  const isSideDrawerOpen = useSelector(
    (state: AppState) => state.nav.isSideDrawerOpen
  );
  const isAdmin = useSelector((state: AppState) => state.user.isStaff);
  const isLoggedIn = useSelector((state: AppState) => state.user.isLoggedIn);
  const authToken = useSelector((state: AppState) => state.user.authToken);
  const myReadsUnreadCount = useSelector((state: AppState) => state.myReads.unreadCount);

  useEffect(() => {
    axios.get('/api/total-blogs/').then(res => {
      setTotalBlogs(res.data.total_blogs);
      setBannedBlogs(res.data.banned_blogs);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    axios.get('/api/newsfeed/').then(res => {
      const latestTimestamp = res.data?.[0]?.timestamp;
      if (!latestTimestamp) return;
      const lastViewed = localStorage.getItem(CONVERSATIONS_LAST_VIEWED_KEY);
      setHasNewConversations(!lastViewed || new Date(latestTimestamp) > new Date(lastViewed));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAdmin || !authToken) return;
    axios
      .get('/api/articles/suggestions/pending-count/', {
        headers: { Authorization: `Token ${authToken}` },
      })
      .then(res => {
        const latestCreatedOn = res.data?.latest_created_on;
        if (!latestCreatedOn) return;
        const lastViewed = localStorage.getItem(ARTICLE_SUGGESTIONS_LAST_VIEWED_KEY);
        setHasNewArticleSuggestions(
          !lastViewed || new Date(latestCreatedOn) > new Date(lastViewed)
        );
      })
      .catch(() => {});
  }, [isAdmin, authToken]);

  useEffect(() => {
    handleNavigateChange(location.pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  useEffect(() => {
    setIsOpen(isSideDrawerOpen);
  }, [isSideDrawerOpen]);

  const markConversationsViewed = () => {
    localStorage.setItem(CONVERSATIONS_LAST_VIEWED_KEY, new Date().toISOString());
    setHasNewConversations(false);
  };

  const markArticleSuggestionsViewed = () => {
    localStorage.setItem(ARTICLE_SUGGESTIONS_LAST_VIEWED_KEY, new Date().toISOString());
    setHasNewArticleSuggestions(false);
  };

  const handleNavigateChange = (currentUrl: string) => {
    deselectAllNavButtons();
    switch (currentUrl) {
      case '/upload-blog':
        setIsUploadSelected(true);
        break;
      case '/create-curated-list':
        setIsCreateCuratedListSelected(true);
        break;
      case '/suggest-article':
        setIsSuggestArticleSelected(true);
        break;
      case '/donate':
        setIsDonateSelected(true);
        break;
      case 'https.thewildernet.substack.com/':
        setIsBlogSelected(true);
        break;
      case '/contact-us':
        setIsContactUsSelected(true);
        break;
      case '/profile':
      case '/edit-profile':
        setIsProfileSelected(true);
        break;
      case '/':
        setIsHomeSelected(true);
        break;
      case '/conversations':
        setIsConversationsSelected(true);
        markConversationsViewed();
        break;
      case '/my-reads':
        setIsMyReadsSelected(true);
        break;
      case '/about':
        setIsAboutSelected(true);
        break;
      case '/admin-wn':
        setIsAdminSelected(true);
        break;
      case '/admin/suggested-articles':
        setIsAdminSelected(true);
        markArticleSuggestionsViewed();
        break;
      default:
        deselectAllNavButtons();
        break;
    }
  };

  const handleHomeNavButtonClick = () => {
    navigate('/');
    closeDrawerIfMobile();
  };
  const handleConversationsNavButtonClick = () => {
    navigate('/conversations');
    closeDrawerIfMobile();
  };

  const handleMyReadsNavButtonClick = () => {
    navigate('/my-reads');
    closeDrawerIfMobile();
  };

  const handleAboutNavButtonClick = () => {
    navigate('/about');
    closeDrawerIfMobile();
  };

  const handleUploadNavButtonClick = async () => {
    navigate('/upload-blog');
    dispatch(resetBlogDetailState());
    closeDrawerIfMobile();
  };
  const handleCreateListClick = async () => {
    navigate('/create-curated-list');
    dispatch(resetBlogDetailState());
    closeDrawerIfMobile();
  };

  const handleSuggestArticleNavButtonClick = () => {
    navigate('/suggest-article');
    closeDrawerIfMobile();
  };

  const handleDonateNavButtonClick = () => {
    navigate('/donate');
    closeDrawerIfMobile();
  };

  const handleContactUsNavButtonClick = () => {
    navigate('/contact-us');
    closeDrawerIfMobile();
  };

  const handleProfileNavButtonClick = () => {
    navigate('/profile');
    closeDrawerIfMobile();
  };
  const handleBlogNavButtonClick = () => {
    window.open('https://thewildernet.substack.com/', '_blank');
    closeDrawerIfMobile();
  };

  const handleAdminNavButtonClick = () => {
    navigate('/admin-wn');
    closeDrawerIfMobile();
  };

  const DESKTOP_BREAKPOINT = 1030;

  const closeDrawerIfMobile = () => {
    if (window.innerWidth < DESKTOP_BREAKPOINT) {
      dispatch(setIsSideDrawerOpen(false));
    }
  };

  const deselectAllNavButtons = () => {
    setIsContactUsSelected(false);
    setIsAboutSelected(false);
    setIsDonateSelected(false);
    setIsHomeSelected(false);
    setIsProfileSelected(false);
    setIsUploadSelected(false);
    setIsCreateCuratedListSelected(false);
    setIsSuggestArticleSelected(false);
    setIsAdminSelected(false);
    setIsConversationsSelected(false);
    setIsMyReadsSelected(false);
    setIsBlogSelected(false);
  };

  const toggleDrawer = () => {
    dispatch(setIsSideDrawerOpen(!isSideDrawerOpen));
  };

  return (
    <div>
      <div
        className={`side-drawer-${whichUserFlow} side-drawer ${
          isSideDrawerOpen ? 'open' : ''
        }`}
      >
        <button
          className="sd-edge-toggle"
          onClick={toggleDrawer}
          aria-label="Toggle sidebar"
        >
          <span className="sd-edge-toggle__icon">
            <MenuIcon />
          </span>
        </button>

        <div className="side-drawer-inner">
        <div className="side-drawer-flex">
          <div>
            <div className="sd-stats">
              <p className="sd-stats__heading">Community Stats</p>
              <div className="sd-stats__stat">
                <div className="sd-stats__number-row">
                  <span className="sd-stats__value">{totalBlogs.toLocaleString()}</span>
                  <span className="sd-stats__suffix">/ 5,000</span>
                </div>
                <div className="sd-stats__label">Blogs on The WilderNet</div>
                <div className="sd-stats__track">
                  <div
                    className="sd-stats__fill"
                    style={{ width: `${Math.min((totalBlogs / 5000) * 100, 100)}%` }}
                  />
                </div>
              </div>
              <div className="sd-stats__h-divider" />
              <div className="sd-stats__stat">
                <div className="sd-stats__number-row">
                  <span className="sd-stats__value">{bannedBlogs.toLocaleString()}</span>
                </div>
                <div className="sd-stats__label">Trash Sites Removed</div>
              </div>
            </div>

          <div className="navigation-section">
              <div className="nav-group">
                {/* <span className="nav-group__label">Main</span> */}
                <SideDrawerButtonComponent
                  buttonText="Home"
                  iconName="home"
                  isSelected={isHomeSelected}
                  handleSideDrawerButtonClick={handleHomeNavButtonClick}
                />
                <SideDrawerButtonComponent
                  buttonText="Conversations"
                  iconName="newsfeed"
                  isSelected={isConversationsSelected}
                  handleSideDrawerButtonClick={handleConversationsNavButtonClick}
                  showUnreadDot={hasNewConversations}
                />
                <SideDrawerButtonComponent
                  buttonText="My Reads"
                  customIcon={<RssFeedIcon />}
                  isSelected={isMyReadsSelected}
                  handleSideDrawerButtonClick={handleMyReadsNavButtonClick}
                  showUnreadDot={isLoggedIn && myReadsUnreadCount > 0}
                />
              </div>

              <div className="nav-group nav-group--secondary">
                {/* <span className="nav-group__label">Create</span> */}
                <SideDrawerButtonComponent
                  buttonText="Add Blog"
                  iconName="upload"
                  isSelected={isUploadSelected}
                  handleSideDrawerButtonClick={handleUploadNavButtonClick}
                />
                <SideDrawerButtonComponent
                  buttonText="Create a Trail"
                  customIcon={<MapOutlinedIcon />}
                  isSelected={isCreateCuratedListSelected}
                  handleSideDrawerButtonClick={handleCreateListClick}
                />
                <SideDrawerButtonComponent
                  buttonText="Suggest an Article"
                  customIcon={<LightbulbOutlinedIcon />}
                  isSelected={isSuggestArticleSelected}
                  handleSideDrawerButtonClick={handleSuggestArticleNavButtonClick}
                />
              </div>

              <div className="nav-group nav-group--secondary">
                <SideDrawerButtonComponent
                  buttonText="Profile"
                  iconName="profile"
                  isSelected={isProfileSelected}
                  handleSideDrawerButtonClick={handleProfileNavButtonClick}
                />
                <SideDrawerButtonComponent
                  buttonText="Donate"
                  iconName="donate"
                  isSelected={isDonateSelected}
                  handleSideDrawerButtonClick={handleDonateNavButtonClick}
                />
                <SideDrawerButtonComponent
                  buttonText="About"
                  iconName="about"
                  isSelected={isAboutSelected}
                  handleSideDrawerButtonClick={handleAboutNavButtonClick}
                />
                <SideDrawerButtonComponent
                  buttonText="Our Substack"
                  iconName="newspaper"
                  isSelected={isBlogSelected}
                  handleSideDrawerButtonClick={handleBlogNavButtonClick}
                />
                <SideDrawerButtonComponent
                  buttonText="Contact Us"
                  iconName="contact-us-icon"
                  isSelected={isContactUsSelected}
                  handleSideDrawerButtonClick={handleContactUsNavButtonClick}
                />
              </div>

              {isAdmin && (
                <div className="nav-group nav-group--admin">
                  <SideDrawerButtonComponent
                    buttonText="Admin"
                    iconName="admin"
                    isSelected={isAdminSelected}
                    handleSideDrawerButtonClick={handleAdminNavButtonClick}
                    showUnreadDot={hasNewArticleSuggestions}
                  />
                </div>
              )}
            </div>
          </div>
          <div className="side-drawer-terms-link">
            <a href={'/assets/pdf/Terms.pdf'} target="_blank" rel="noreferrer">
              <SideDrawerButtonComponent buttonText="Terms and Agreement" />
            </a>
            <a
              href={'/assets/pdf/Community-Guidelines.pdf'}
              target="_blank"
              rel="noreferrer"
            >
              <SideDrawerButtonComponent buttonText="Community Guidelines" />
            </a>
          </div>
        </div>
        </div> {/* side-drawer-inner */}
      </div>

      <Backdrop
        customClass="side-drawer-backdrop"
        show={isSideDrawerOpen}
        handleClick={toggleDrawer}
      />
    </div>
  );
};

export default SideDrawer;